import React, { useState } from 'react';
import { Container, Card, Button, Title, Select, Input, ScoreGrid, ScoreCard, Subtitle, RoundList, RoundItem, PlayerSelectionButton, ThreeStatePlayerButton } from '../../styles/GlobalStyles';
import { GAME_TYPES } from '../../data/gameTypes';
import { calculateScoreChanges, getWinner, determineGameResult, formatScore } from '../../utils/gameLogic';
import ScoreGraph from './ScoreGraph';

const GameActive = ({ game, onUpdateGame, onEndGame, onBackToMenu, translations, language }) => {
  const [selectedGameType, setSelectedGameType] = useState('');
  const [selectedInitiators, setSelectedInitiators] = useState([]);
  const [tricksWon, setTricksWon] = useState('');
  const [manualResult, setManualResult] = useState(null); // Only for games that can't be determined automatically
  const [showGraph, setShowGraph] = useState(false); // Toggle between list and graph view
  const [playerStates, setPlayerStates] = useState({}); // For three-state games
  const [mateLosesOnFailure, setMateLosesOnFailure] = useState(false); // Option for mate losing points in Rikken
  const [showEndGameConfirm, setShowEndGameConfirm] = useState(false); // Show end game confirmation

  const t = translations[language];

  const handleGameTypeChange = (gameTypeId) => {
    const gameType = GAME_TYPES.find(gt => gt.id === parseInt(gameTypeId));
    setSelectedGameType(gameType);
    setSelectedInitiators([]);
    setTricksWon(gameType?.baseTarget?.toString() || '');
    setManualResult(null);
    setPlayerStates({});
  };

  const handleInitiatorToggle = (playerId) => {
    if (!selectedGameType) return;

    const maxInitiators = selectedGameType.maxPlayers;
    const currentInitiators = [...selectedInitiators];

    if (currentInitiators.includes(playerId)) {
      setSelectedInitiators(currentInitiators.filter(id => id !== playerId));
    } else if (currentInitiators.length < maxInitiators) {
      setSelectedInitiators([...currentInitiators, playerId]);
    }
  };

  const handleThreeStatePlayerToggle = (playerId) => {
    const currentState = playerStates[playerId] || 'neutral';
    let newState;

    // Cycle through states: neutral -> won -> lost -> neutral
    if (currentState === 'neutral') {
      newState = 'won';
    } else if (currentState === 'won') {
      newState = 'lost';
    } else {
      newState = 'neutral';
    }

    setPlayerStates(prev => ({
      ...prev,
      [playerId]: newState
    }));
  };

  const canAddRound = () => {
    if (!selectedGameType) return false;

    // Handle three-state games
    if (selectedGameType.supportsThreeState) {
      const states = Object.values(playerStates);
      const hasWinner = states.includes('won');
      const hasLoser = states.includes('lost');

      // For three-state games, we need at least one player with a defined state (won or lost)
      // and not everyone can be neutral
      const definedStates = states.filter(state => state === 'won' || state === 'lost');
      return definedStates.length > 0 && definedStates.length < game.players.length;
    }

    // Handle regular games
    if (selectedInitiators.length === 0) return false;
    if (selectedGameType.supportsTricks && tricksWon === '') return false;

    // For games that can't be automatically determined, we need manual input
    if (selectedGameType.supportsTricks) {
      const autoResult = determineGameResult(selectedGameType, parseInt(tricksWon));
      if (autoResult === null && manualResult === null) return false;
    }

    return selectedInitiators.length >= selectedGameType.minPlayers &&
      selectedInitiators.length <= selectedGameType.maxPlayers;
  };

  const handleAddRound = () => {
    if (!canAddRound()) return;

    let newPlayers;
    let gameWon = null;

    if (selectedGameType.supportsThreeState) {
      // Handle three-state games
      newPlayers = calculateScoreChanges(
        game,
        selectedGameType,
        [], // No initiators for three-state games
        null, // No won/lost for three-state games
        undefined, // No tricks for three-state games
        playerStates,
        mateLosesOnFailure
      );
    } else {
      // Handle regular games
      // Determine if the game was won automatically or use manual result
      if (selectedGameType.supportsTricks) {
        const autoResult = determineGameResult(selectedGameType, parseInt(tricksWon));
        gameWon = autoResult !== null ? autoResult : manualResult;
      } else {
        gameWon = manualResult;
      }

      newPlayers = calculateScoreChanges(
        game,
        selectedGameType,
        selectedInitiators,
        gameWon,
        selectedGameType.supportsTricks ? parseInt(tricksWon) : undefined,
        null,
        mateLosesOnFailure
      );
    }

    const newRound = {
      id: Date.now(),
      gameType: selectedGameType,
      initiators: selectedGameType.supportsThreeState ? [] : selectedInitiators,
      tricksWon: selectedGameType.supportsTricks ? parseInt(tricksWon) : undefined,
      won: gameWon,
      playerStates: selectedGameType.supportsThreeState ? { ...playerStates } : undefined,
      scoreChanges: newPlayers.map((player, index) => ({
        playerId: player.id,
        oldScore: game.players[index].score,
        newScore: player.score,
        change: player.score - game.players[index].score
      }))
    };

    const updatedGame = {
      ...game,
      players: newPlayers,
      rounds: [...game.rounds, newRound],
      currentRound: game.currentRound + 1,
      lastUpdated: new Date()
    };

    onUpdateGame(updatedGame);

    // Reset form for next round
    setSelectedGameType('');
    setSelectedInitiators([]);
    setTricksWon('');
    setManualResult(null);
    setPlayerStates({});
  };

  const handleEndGameClick = () => {
    setShowEndGameConfirm(true);
  };

  const handleConfirmEndGame = () => {
    setShowEndGameConfirm(false);
    onEndGame();
  };

  const handleCancelEndGame = () => {
    setShowEndGameConfirm(false);
  };

  const winner = getWinner(game.players);

  return (
    <Container>
      <Title>{t.appTitle}</Title>

      <Card>
        <Subtitle>{t.currentScores}</Subtitle>
        <ScoreGrid>
          {game.players.map(player => (
            <ScoreCard key={player.id}>
              <h3 style={{ position: 'relative' }}>
                {player.name}
                {winner?.id === player.id && (
                  <span style={{
                    fontSize: '3.6em',
                    position: 'absolute',
                    // right: '-24px',
                    right: '5px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    lineHeight: '1'
                  }}>♚</span>
                )}
              </h3>
              <div className="score">{Math.round(player.score)}</div>
            </ScoreCard>
          ))}
        </ScoreGrid>
      </Card>

      {game.rounds.length > 0 && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <Subtitle>{t.rounds} ({game.rounds.length})</Subtitle>
            {game.rounds.length > 1 && (
              <Button
                variant="secondary"
                onClick={() => setShowGraph(!showGraph)}
              // style={{ padding: '4px 8px', fontSize: '10px' }}
              >
                {showGraph ? t.showList || 'Show List' : t.showGraph || 'Show Graph'}
              </Button>
            )}
          </div>

          {showGraph && game.rounds.length > 1 ? (
            <ScoreGraph game={game} translations={translations} language={language} />
          ) : (
            <RoundList>
              {game.rounds.slice().reverse().map((round, index) => (
                <RoundItem key={round.id} won={round.won}>
                  <div className="round-header">
                    <span>{round.gameType.name}</span>
                    {/* <span style={{ color: round.won ? '#4CAF50' : '#dc3545' }}>
                      {round.won ? t.won : t.lost}
                    </span> */}
                  </div>
                  <div className="round-details">
                    {round.gameType.supportsThreeState ? (
                      <div>
                        {t.playerState}: {Object.entries(round.playerStates || {}).map(([playerId, state]) => {
                          const player = game.players.find(p => p.id === parseInt(playerId));
                          const stateText = state === 'won' ? t.won : state === 'lost' ? t.lost : t.neutral;
                          return `${player?.name}: ${stateText}`;
                        }).join(', ')}
                      </div>
                    ) : (
                      <div>
                        {t.initiator}: {round.initiators.map(id =>
                          game.players.find(p => p.id === id)?.name
                        ).join(', ')}
                      </div>
                    )}
                    {round.tricksWon !== undefined && (
                      <div>{t.tricksWon}: {round.tricksWon}</div>
                    )}
                  </div>
                </RoundItem>
              ))}
            </RoundList>
          )}
        </Card>
      )}

      <Card>
        <Subtitle>{t.addRound}</Subtitle>

        <Select
          value={selectedGameType?.id || ''}
          onChange={(e) => handleGameTypeChange(e.target.value)}
        >
          <option value="">{t.selectGameType}</option>
          {GAME_TYPES.map(gameType => (
            <option key={gameType.id} value={gameType.id}>
              {gameType.name} ({gameType.points}p)
            </option>
          ))}
        </Select>

        {selectedGameType && (
          <>
            {selectedGameType.supportsThreeState ? (
              // pieken/misèren (open met praatje)
              <div style={{ marginBottom: '15px' }}>
                {/* <h4 style={{ marginBottom: '10px' }}>{t.selectPlayerStates}</h4> */}
                {/* here */}
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginBottom: '5px' }}>
                  Selecteer {selectedGameType.minPlayers === selectedGameType.maxPlayers
                    ? `${selectedGameType.minPlayers} speler${selectedGameType.minPlayers > 1 ? 's' : ''}`
                    : `${selectedGameType.minPlayers}-${selectedGameType.maxPlayers} speler(s)`
                  }
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {game.players.map((player) => {
                    const state = playerStates[player.id] || 'neutral';
                    const stateText = state === 'won' ? t.won : state === 'lost' ? t.lost : t.neutral;

                    return (
                      <ThreeStatePlayerButton
                        key={player.id}
                        state={state}
                        onClick={() => handleThreeStatePlayerToggle(player.id)}
                      >
                        {player.name} - {stateText}
                      </ThreeStatePlayerButton>
                    );
                  })}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {t.clickToChangeState || 'Klik om status te wijzigen'}<br />
                  {/* <small>
                    Won = speler heeft het spel gehaald, Lost = speler heeft het spel geprobeerd maar gefaald, Neutral = speler heeft niet meegedaan
                  </small> */}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '15px' }}>
                {/* <h4 style={{ marginBottom: '10px' }}>{t.selectInitiators}</h4> */}

                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginBottom: '5px' }}>
                  Selecteer {selectedGameType.minPlayers === selectedGameType.maxPlayers
                    ? `${selectedGameType.minPlayers} speler${selectedGameType.minPlayers > 1 ? 's' : ''}`
                    : `${selectedGameType.minPlayers}-${selectedGameType.maxPlayers} speler(s)`
                  }
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {game.players.map((player, index) => {
                    const isSelected = selectedInitiators.includes(player.id);
                    const canSelect = isSelected || selectedInitiators.length < selectedGameType.maxPlayers;

                    return (
                      <PlayerSelectionButton
                        key={player.id}
                        playerIndex={index}
                        selected={isSelected}
                        disabled={!canSelect}
                        onClick={() => canSelect && handleInitiatorToggle(player.id)}
                      >
                        {player.name}
                      </PlayerSelectionButton>
                    );
                  })}
                </div>

                {/* Option for mate losing points in Rikken games */}
                {selectedGameType.requiresMate && (
                  <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={mateLosesOnFailure}
                        onChange={(e) => setMateLosesOnFailure(e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ fontSize: '14px' }}>
                        {t.mateLosesOnFailure || 'Maat verliest ook punten bij falen'}
                      </span>
                    </label>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {t.mateLosesOnFailureDescription || 'Officiële regels: maat verliest nooit punten. Met deze optie verliest de maat ook punten bij falen.'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedGameType.supportsTricks && (
              <div style={{ display: 'flex', alignItems: 'stretch', gap: 'calc(2px * var(--global-scale))', marginBottom: '15px' }}>
                <Input
                  type="number"
                  placeholder={`${t.tricksWon} (doel: ${selectedGameType.baseTarget})`}
                  value={tricksWon}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string for clearing the input
                    if (value === '') {
                      setTricksWon('');
                      return;
                    }

                    const numValue = parseInt(value);
                    // Only allow values between 0 and 13
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 13) {
                      setTricksWon(value);
                    }
                  }}
                  min="0"
                  max="13"
                  style={{
                    flex: 1,
                    marginBottom: 0,
                    /* Hide number input spin buttons */
                    MozAppearance: 'textfield', /* Firefox */
                    WebkitAppearance: 'none', /* Safari/Chrome */
                    appearance: 'none' /* Standard */
                  }}
                  css={`
                    /* Hide spin buttons in WebKit browsers */
                    &::-webkit-outer-spin-button,
                    &::-webkit-inner-spin-button {
                      -webkit-appearance: none;
                      margin: 0;
                    }
                    
                    /* Hide spin buttons in Firefox */
                    &[type=number] {
                      -moz-appearance: textfield;
                    }
                  `}
                />
                <button
                  type="button"
                  onClick={() => {
                    const currentValue = tricksWon === '' ? 0 : parseInt(tricksWon);
                    if (currentValue > 0) {
                      setTricksWon((currentValue - 1).toString());
                    }
                  }}
                  disabled={tricksWon === '' || parseInt(tricksWon) <= 0}
                  style={{
                    width: 'calc(40px * var(--global-scale))',
                    minWidth: 'calc(40px * var(--global-scale))',
                    height: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    border: 'calc(2px * var(--global-scale)) solid #000',
                    padding: 'calc(4px * var(--global-scale))',
                    cursor: tricksWon === '' || parseInt(tricksWon) <= 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'calc(14px * var(--global-scale))',
                    fontWeight: 'bold',
                    color: tricksWon === '' || parseInt(tricksWon) <= 0 ? '#666' : '#000',
                    fontFamily: 'inherit',
                    outline: 'none',
                    opacity: tricksWon === '' || parseInt(tricksWon) <= 0 ? 0.6 : 1
                  }}
                  onFocus={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.borderColor = '#000';
                      e.target.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                >
                  −
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const currentValue = tricksWon === '' ? 0 : parseInt(tricksWon);
                    if (currentValue < 13) {
                      setTricksWon((currentValue + 1).toString());
                    }
                  }}
                  disabled={tricksWon !== '' && parseInt(tricksWon) >= 13}
                  style={{
                    width: 'calc(40px * var(--global-scale))',
                    minWidth: 'calc(40px * var(--global-scale))',
                    height: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    border: 'calc(2px * var(--global-scale)) solid #000',
                    padding: 'calc(4px * var(--global-scale))',
                    cursor: tricksWon !== '' && parseInt(tricksWon) >= 13 ? 'not-allowed' : 'pointer',
                    fontSize: 'calc(14px * var(--global-scale))',
                    fontWeight: 'bold',
                    color: tricksWon !== '' && parseInt(tricksWon) >= 13 ? '#666' : '#000',
                    fontFamily: 'inherit',
                    outline: 'none',
                    opacity: tricksWon !== '' && parseInt(tricksWon) >= 13 ? 0.6 : 1
                  }}
                  onFocus={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.borderColor = '#000';
                      e.target.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                >
                  +
                </button>
              </div>
            )}

            {/* Show automatic result for trick-based games that can be determined */}
            {selectedGameType.supportsTricks && tricksWon !== '' && (() => {
              const result = determineGameResult(selectedGameType, parseInt(tricksWon));
              if (result !== null) {
                const target = selectedGameType.baseTarget || 0;
                return (
                  <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '5px' }}>{t.result}</h4>
                    <div style={{
                      color: result ? '#4CAF50' : '#dc3545',
                      fontWeight: 'bold'
                    }}>
                      {result ? t.gameWon : t.gameLost}
                      <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '10px' }}>
                        ({tricksWon}/{target} slagen)
                      </span>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Manual result input for games that can't be automatically determined */}
            {selectedGameType.supportsTricks && tricksWon !== '' && determineGameResult(selectedGameType, parseInt(tricksWon)) === null && (
              <div style={{ marginBottom: '15px' }}>
                <h4 style={{ marginBottom: '10px' }}>{t.result}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  <Button
                    variant={manualResult === true ? 'success' : 'secondary'}
                    onClick={() => setManualResult(true)}
                    style={{ marginBottom: '5px' }}
                  >
                    {t.gameWon}
                  </Button>
                  <Button
                    variant={manualResult === false ? 'danger' : 'secondary'}
                    onClick={() => setManualResult(false)}
                    style={{ marginBottom: '5px' }}
                  >
                    {t.gameLost}
                  </Button>
                </div>
              </div>
            )}

            <Button
              onClick={handleAddRound}
              disabled={!canAddRound()}
              variant={canAddRound() ? 'success' : 'secondary'}
            >
              {t.confirm}
            </Button>
          </>
        )}

        <Button variant="secondary" onClick={handleEndGameClick}>
          {t.endGame}
        </Button>
        <Button variant="secondary" onClick={onBackToMenu}>
          {t.menu}
        </Button>
      </Card>

      {/* End Game Confirmation Dialog */}
      {showEndGameConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <Card style={{
            width: '100%',
            maxWidth: '700px',
            backgroundColor: 'white',
            boxSizing: 'border-box'
          }}>
            <Subtitle>{t.confirmEndGame}</Subtitle>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              {t.confirmEndGameDescription}
            </p>
            <div style={{
              display: 'flex',
              gap: '10px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <Button
                variant="danger"
                onClick={handleConfirmEndGame}
                style={{
                  flex: 1,
                  minWidth: 0,
                  boxSizing: 'border-box'
                }}
              >
                {t.endGame}
              </Button>
              <Button
                variant="secondary"
                onClick={handleCancelEndGame}
                style={{
                  flex: 1,
                  minWidth: 0,
                  boxSizing: 'border-box'
                }}
              >
                {t.cancel}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Container>
  );
};

export default GameActive;
